<template>
    <b-modal ref="modal" title="Bulk Edit Auto Process Products" size="lg" @ok.prevent="save"
        :ok-disabled="saving" ok-title="Apply Changes" cancel-title="Cancel" no-close-on-backdrop>
        <p class="text-muted mb-3">
            Editing <strong>{{ selectedCount }}</strong> product{{ selectedCount !== 1 ? 's' : '' }}.
            Only fields set to a value will be updated. Fields left as "Keep Current" are unchanged.
        </p>

        <!-- Boolean Fields -->
        <div class="mb-3">
            <strong>Toggle Settings</strong>
            <b-form-row class="mt-2">
                <b-col md="4" v-for="field in booleanFields" :key="field.key">
                    <label>
                        {{ field.label }}
                        <small v-if="field.key === 'createPartialPaymentOrder' && !partialPaymentEditable"
                               class="text-muted ml-1">— requires Autoship = Yes</small>
                    </label>
                    <b-select v-model="boolValues[field.key]" size="sm" :options="triStateOptions"
                              :disabled="field.key === 'createPartialPaymentOrder' && !partialPaymentEditable" />
                </b-col>
            </b-form-row>
        </div>

        <hr />

        <!-- Value Fields -->
        <div class="mb-3">
            <strong>Value Settings</strong>
            <b-form-row class="mt-2">
                <b-col md="6">
                    <label>Days Supply Factor</label>
                    <div class="d-flex gap-2">
                        <b-select v-model="setDaysSupplyFactor" :options="setOrKeepOptions" size="sm"
                            class="w-auto" />
                        <b-input v-if="setDaysSupplyFactor" v-model.number="daysSupplyFactor" type="number"
                            step="0.1" min="0" size="sm" />
                    </div>
                </b-col>
                <b-col md="6">
                    <label>Default Delivery</label>
                    <div class="d-flex gap-2">
                        <b-select v-model="setDefaultDelivery" :options="setOrKeepOptions" size="sm"
                            class="w-auto" />
                        <b-form-select v-if="setDefaultDelivery" v-model="defaultDelivery" size="sm"
                            :options="deliveryCodeOptions" />
                    </div>
                </b-col>
            </b-form-row>
            <b-form-row class="mt-2">
                <b-col md="6">
                    <label>Webhook URL</label>
                    <div class="d-flex gap-2">
                        <b-select v-model="setWebhookURL" :options="setOrKeepOptions" size="sm"
                            class="w-auto" />
                        <b-input v-if="setWebhookURL" v-model="webhookURL" size="sm" placeholder="https://" />
                    </div>
                </b-col>
                <b-col md="6">
                    <label>Default Directions</label>
                    <div class="d-flex gap-2">
                        <b-select v-model="setDefaultDirections" :options="setOrKeepOptions" size="sm"
                            class="w-auto" />
                        <b-input v-if="setDefaultDirections" v-model="defaultDirections" size="sm" />
                    </div>
                </b-col>
            </b-form-row>
            <b-form-row class="mt-2">
                <b-col md="6">
                    <label>Default Cash Out Price Schedule</label>
                    <div class="d-flex gap-2">
                        <b-select v-model="setDefaultCashOut" :options="setOrKeepOptions" size="sm"
                            class="w-auto" />
                        <PriceScheduleSelect v-if="setDefaultCashOut" v-model="defaultCashOut" />
                    </div>
                </b-col>
                <b-col md="6">
                    <label>On Hold Sub-Status</label>
                    <div class="d-flex gap-2">
                        <b-select v-model="setOnHoldSubStatusId" :options="setOrKeepOptions" size="sm"
                            class="w-auto" />
                        <OnHoldSubStatusSelect v-if="setOnHoldSubStatusId" v-model="onHoldSubStatusId" />
                    </div>
                </b-col>
            </b-form-row>
        </div>
    </b-modal>
</template>

<script lang="ts">
import axios from 'axios';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { AutoProcessProduct, AutoProcessBulkUpdateRequest } from '@/models/AutoProcessProduct';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import OnHoldSubStatusSelect from '@/components/Program/OnHoldSubStatusSelect.vue';
import PriceScheduleSelect from '@/components/Program/PriceScheduleSelect.vue';
import { DeliveryCode } from '@/models/DeliveryCode';
import { enumToOptions } from '@/util/EnumOptions';

type TriState = null | true | false;

@Component({
    name: 'AutoProcessBulkEditModal',
    components: { OnHoldSubStatusSelect, PriceScheduleSelect },
})
export default class AutoProcessBulkEditModal extends Vue {
    @Prop({ required: true }) programId!: number;

    private selectedProducts: AutoProcessProduct[] = [];
    private saving = false;

    // Boolean tri-state fields (null = keep current)
    private boolValues: Record<string, TriState> = {
        autoship: null,
        onHold: null,
        passthroughSig: null,
        selectBasedOnInventory: null,
        isConsignment: null,
        autoBillInsurance: null,
        createPartialPaymentOrder: null,
    };

    // Value fields with set/keep toggle
    private setDaysSupplyFactor = false;
    private daysSupplyFactor = 1.0;
    private setDefaultDelivery = false;
    private defaultDelivery: number | null = null;
    private setWebhookURL = false;
    private webhookURL = '';
    private setDefaultDirections = false;
    private defaultDirections = '';
    private setDefaultCashOut = false;
    private defaultCashOut: number | null = null;
    private setOnHoldSubStatusId = false;
    private onHoldSubStatusId: number | null = null;

    // Numeric DeliveryCode values pair with the worker's (int)Prescription.DeliveryCodeEnum
    // cast in CreateOrderForAutoShip. Bulk modal previously used <EnumSelector enum-name=>
    // which doesn't accept that prop shape — the dropdown rendered empty so this control
    // was non-functional.
    private deliveryCodeOptions = enumToOptions(DeliveryCode, { includeNone: true });

    private booleanFields = [
        { key: 'autoship', label: 'Autoship' },
        { key: 'onHold', label: 'On Hold' },
        { key: 'passthroughSig', label: 'Passthrough Sig' },
        { key: 'selectBasedOnInventory', label: 'Select Based On Inventory' },
        { key: 'isConsignment', label: 'Is Consignment' },
        { key: 'autoBillInsurance', label: 'Auto Bill Insurance' },
        { key: 'createPartialPaymentOrder', label: 'Partial Payment Order' },
    ];

    private triStateOptions = [
        { value: null, text: 'Keep Current' },
        { value: true, text: 'Set to Yes' },
        { value: false, text: 'Set to No' },
    ];

    private setOrKeepOptions = [
        { value: false, text: 'Keep Current' },
        { value: true, text: 'Set Value' },
    ];

    get selectedCount(): number {
        return this.selectedProducts.length;
    }

    // The worker only honours createPartialPaymentOrder when the row also has Autoship=true.
    // In bulk-edit context Autoship may be "Keep Current" (varies per row, can't tell), "Set
    // to No" (would null the partial-payment effect), or "Set to Yes". We only let the user
    // change createPartialPaymentOrder when they're also explicitly setting Autoship=Yes —
    // otherwise the flag would silently no-op on rows that don't have Autoship enabled.
    get partialPaymentEditable(): boolean {
        return this.boolValues.autoship === true;
    }

    @Watch('boolValues.autoship')
    onAutoshipChanged(val: TriState) {
        if (val !== true) this.boolValues.createPartialPaymentOrder = null;
    }

    open(products: AutoProcessProduct[]) {
        this.selectedProducts = products;
        this.resetFields();
        (this.$refs.modal as any).show();
    }

    private resetFields() {
        for (const key of Object.keys(this.boolValues)) {
            this.boolValues[key] = null;
        }
        this.setDaysSupplyFactor = false;
        this.daysSupplyFactor = 1.0;
        this.setDefaultDelivery = false;
        this.defaultDelivery = null;
        this.setWebhookURL = false;
        this.webhookURL = '';
        this.setDefaultDirections = false;
        this.defaultDirections = '';
        this.setDefaultCashOut = false;
        this.defaultCashOut = null;
        this.setOnHoldSubStatusId = false;
        this.onHoldSubStatusId = null;
    }

    async save() {
        this.saving = true;
        try {
            const request: AutoProcessBulkUpdateRequest = {
                programId: this.programId,
                ids: this.selectedProducts.map(p => p.id),
                autoship: this.boolValues.autoship,
                onHold: this.boolValues.onHold,
                passthroughSig: this.boolValues.passthroughSig,
                selectBasedOnInventory: this.boolValues.selectBasedOnInventory,
                isConsignment: this.boolValues.isConsignment,
                autoBillInsurance: this.boolValues.autoBillInsurance,
                createPartialPaymentOrder: this.boolValues.createPartialPaymentOrder,
                daysSupplyFactor: this.setDaysSupplyFactor ? this.daysSupplyFactor : null,
                defaultDelivery: this.setDefaultDelivery ? this.defaultDelivery : null,
                setDefaultDelivery: this.setDefaultDelivery,
                webhookURL: this.setWebhookURL ? this.webhookURL : null,
                setWebhookURL: this.setWebhookURL,
                defaultDirections: this.setDefaultDirections ? this.defaultDirections : null,
                setDefaultDirections: this.setDefaultDirections,
                defaultCashOut: this.setDefaultCashOut ? this.defaultCashOut : null,
                setDefaultCashOut: this.setDefaultCashOut,
                onHoldSubStatusId: this.setOnHoldSubStatusId ? this.onHoldSubStatusId : null,
                setOnHoldSubStatusId: this.setOnHoldSubStatusId,
            };

            const { data } = await axios.put(
                `/api/program/${this.programId}/auto-process-products/bulk`,
                request
            );

            (this as any).$notifications?.notify(
                NotificationOptions.success(`${data.rowsAffected} product(s) updated.`)
            );
            (this.$refs.modal as any).hide();
            this.$emit('saved');
        } catch (e: any) {
            (this as any).$notifications?.notify(NotificationOptions.error(e));
        } finally {
            this.saving = false;
        }
    }
}
</script>

<style scoped>
.gap-2 {
    gap: 0.5rem;
}
</style>
